import React, { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import { C, TABLE, DAY_THEMES } from "../constants";
import { parseDayKey } from "../utils";
import { dailySheetsForLayout, appendDailySheetsToWorkbook } from "../utils/weekScheduleGrid";
import { refinePlan, saveVersion } from "../api";
import { useAppNotify } from "./AppNotify";
import Card from "./Card";
import Btn from "./Btn";
import { PlanTable } from "./PlanTable";
import DailyPlanTable from "./DailyPlanTable";
import PlanVersionPanel from "./PlanVersionPanel";
import { parsePlanTaskLines as parseTaskLines } from "../utils/planTaskLines";
import { buildWeeklyFromDaily } from "../utils/weeklyFromDaily";
import { applyCurrentActivityTitles } from "../utils/activityExport";
import { visibleDailyBlueprint } from "../constants/dayActivities";
import { filterAoaByIndexes, loadTablePrefs } from "../utils/tablePersonalize";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const DEFAULT_THEME = { color: "#4B5563", light: "#F3F4F6", label: "📅 Day" };

function applyTeamDailyColumnPrefs(sheets, layout) {
  const prefs = loadTablePrefs(layout === "dayList" ? "daily-list" : "daily-grid");
  const hidden = new Set(prefs.hidden || []);
  const labels = prefs.labels || {};
  return (sheets || []).map((sheet) => {
    const ids = layout === "dayList" ? ["time", "activity", "content"] : ["day", ...(sheet.columns || []).map((c) => c.id)];
    const keep = ids.map((id, i) => hidden.has(id) ? -1 : i).filter((i) => i >= 0);
    const header = (sheet.header || []).map((value, i) => String(labels[ids[i]] || "").trim() || value);
    return { ...sheet, header: filterAoaByIndexes([header], keep.length ? keep : ids.map((_, i) => i))[0], body: filterAoaByIndexes(sheet.body || [], keep.length ? keep : ids.map((_, i) => i)) };
  });
}

// ── Shared row coloring — identical hex values to DailyPlanTable's getRowStyle,
// with a few extra categories (placement/retro/standup) that only ever appear
// in team-generated schedules. ─────────────────────────────────────────────
function neatActivityTitle(raw) {
  const a = String(raw || "").toLowerCase();
  if (/standup|stand[\s-]?up|kickoff/.test(a)) return "Stand-Up";
  if (/lunch/.test(a)) return "Lunch";
  if (/refresh|hollywoodlywood|bollywood|pictionary|🎮/.test(a)) return "Refresh Game";
  if (/problem review|problem statement review/.test(a)) return "Problem Review";
  if (/product speak|🗣️/.test(a)) return "Speak & Solve";
  if (/placement|🎯/.test(a)) return "Placement Prep";
  if (/retro|retrospective/.test(a)) return "Retrospective";
  if (/mini build|interest build|peer review|capability|interests?|goals?|domain/.test(a)) {
    return "Mini Build";
  }
  if (/3c|speak\s*&\s*solve|🎤|aptitude/.test(a)) return "Speak & Solve";
  if (/dsa|coding|🧮/.test(a)) return "Coding Practice";
  if (/system design|🏗️/.test(a)) return "System Design";
  if (/project push|micro-push/.test(a)) return "Project Push";
  if (/project|dt playbook|implement|💻/.test(a)) return "Project Build";
  if (/learning|📖|📚/.test(a)) return "Learning";
  if (/break|☕|🧘/.test(a)) return "Break";
  if (/group/.test(a)) return "Group Work";
  return String(raw || "").replace(/^[\s\p{Emoji_Presentation}\p{Extended_Pictographic}]+/u, "").replace(/\s*[—–-]\s*.+$/, "").trim() || "Activity";
}

const getRowStyle = (activity = "") => {
  const text = neatActivityTitle(activity).toLowerCase();
  if (text.includes("learning")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary };
  if (text.includes("project")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("coding") || text.includes("dsa")) return { bg: C.warningBg, accent: C.primaryDark, dot: C.primary };
  if (text.includes("placement")) return { bg: C.primarySoft, accent: C.primaryDark, dot: C.primary };
  if (text.includes("product") || text.includes("speak")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary };
  if (text.includes("refresh") || text.includes("game")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("break")) return { bg: "#F3F4F6", accent: "#6B7280", dot: "#6B7280" };
  if (text.includes("lunch")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary };
  if (text.includes("retro")) return { bg: C.successBg, accent: C.success, dot: C.success };
  if (text.includes("stand")) return { bg: C.successBg, accent: C.success, dot: C.success };
  return { bg: "#FFFFFF", accent: C.text, dot: C.faint };
};

// Pulls the real weekday ("Monday".."Friday") out of a dayId/dayName string
// like "Week 3 - Wednesday (11-06-2026)" so we can reuse DAY_THEMES exactly
// the way the solo DailyPlanTable does.
function weekdayOf(day) {
  const raw = String(day.dayName || day.dayId || "");
  const m = raw.match(/Monday|Tuesday|Wednesday|Thursday|Friday/i);
  if (m) return m[0].charAt(0).toUpperCase() + m[0].slice(1).toLowerCase();
  return WEEKDAYS[((day.day || day.dayNum || 1) - 1) % 5];
}

function dayLabelOf(day) {
  const raw = String(day.dayName || "").trim();
  if (raw) return raw.replace(/^W(?:eek\s*)?\d+\s*[-–]\s*/i, "").trim();
  return weekdayOf(day);
}

// Flattens one member's per-day rows into the SAME [dayId, time, activity,
// content] shape as the solo daily plan's `plan.rows` — lets us reuse the
// solo Excel/refine logic (and, for weekly, the derived-summary logic)
// unchanged.
function memberFlatRows(schedule, memberId) {
  const mid = String(memberId || "");
  const flat = [];
  (schedule || []).forEach(day => {
    const a = (day.assignments || []).find(x => String(x.memberId) === mid);
    if (!a) return;
    (a.rows || []).forEach(r => {
      flat.push([day.dayId || day.dayName || String(day.day), r.time, r.activity, r.content]);
    });
  });
  return flat;
}

function applyRefinedRows(schedule, memberId, refinedRows) {
  let idx = 0;
  return (schedule || []).map(day => {
    const assignments = (day.assignments || []).map(a => {
      if (a.memberId !== memberId && String(a.memberId) !== String(memberId)) return a;
      const newRows = (a.rows || []).map(r => {
        const refined = refinedRows[idx];
        idx += 1;
        if (!refined) return r;
        return { time: refined[1] ?? r.time, activity: refined[2] ?? r.activity, content: refined[3] ?? r.content };
      });
      return { ...a, rows: newRows };
    });
    return { ...day, assignments };
  });
}

// ── Weekly summary, derived locally from the member's own rows (no LLM
// round-trip needed since this is presentational) — groups by week and
// buckets each row's first bullet into Subjects / Milestone / Exam Focus,
// matching the solo weekly plan's 5-column shape exactly. ──────────────────
function firstBullet(content) {
  const m = String(content || "").match(/▶\s*([^\n]{5,})/);
  if (m) return m[1].trim().slice(0, 140);
  const line = String(content || "").split("\n").map(s => s.trim()).find(Boolean);
  return line ? line.slice(0, 140) : null;
}

function buildMemberWeeklyRows(schedule, memberId) {
  const mid = String(memberId || "");
  const byWeek = new Map();
  (schedule || []).forEach(day => {
    const a = (day.assignments || []).find(x => String(x.memberId) === mid);
    if (!a) return;
    const week = day.week || 1;
    if (!byWeek.has(week)) byWeek.set(week, { week, dayNums: [], subjects: [], milestones: [], exam: [] });
    const bucket = byWeek.get(week);
    const dayNum = day.day || day.dayNum;
    if (dayNum) bucket.dayNums.push(dayNum);
    (a.rows || []).forEach(r => {
      const act = String(r.activity || "").toLowerCase();
      const bullet = firstBullet(r.content);
      if (!bullet) return;
      if (/learning|📖/.test(act))            bucket.subjects.push(bullet);
      else if (/dsa|🧮/.test(act))             bucket.subjects.push(`DSA: ${bullet}`);
      else if (/project|implement|💻/.test(act)) bucket.milestones.push(bullet);
      else if (/exam|internal assessment|📝/.test(act)) bucket.exam.push(bullet);
      else if (/placement|🎯/.test(act))       bucket.exam.push(`Placement: ${bullet}`);
    });
  });
  const uniq = arr => [...new Set(arr)];
  return [...byWeek.values()].sort((a, b) => a.week - b.week).map(b => {
    const days = b.dayNums.length ? `Day ${Math.min(...b.dayNums)}-${Math.max(...b.dayNums)}` : "";
    return [
      `Week ${b.week}`,
      days,
      uniq(b.subjects).slice(0, 6).map(s => `▶ ${s}`).join("\n") || "—",
      uniq(b.milestones).slice(0, 4).map(s => `▶ ${s}`).join("\n") || "—",
      uniq(b.exam).slice(0, 4).map(s => `▶ ${s}`).join("\n") || "—",
    ];
  });
}

const WEEKLY_COLS = ["Week", "Days", "Subjects Covered", "Project Milestone", "Exam Focus"];

/**
 * Gives ONE team member's schedule the exact same chrome as the main solo
 * scheduler (ModuleCard + DailyPlanTable / PlanTable): same header gradient,
 * same day/week pill colors, same table column widths/fonts/row colors,
 * same Excel / CSV / Smart Refinement toolbar — for BOTH the Daily and the
 * Weekly view.
 *
 * Sync / Lock / Disruption are only enabled when `enableDayActions` is true
 * (the logged-in user's own view). Teammate views keep export + refine only,
 * so their actions never overwrite your DailyPlan documents.
 *
 * @param {object}   member          - {id, name, targetRole, targetCompany, skillTag}
 * @param {object[]} schedule        - teamSchedule.schedule (all members, all days)
 * @param {"daily"|"weekly"} module  - which view to render
 * @param {function} onScheduleUpdate - (updatedSchedule) => void — persists a daily refine
 * @param {boolean}  enableDayActions - show Sync / Lock / Meeting Disruption (self only)
 * @param {object}   calMap
 * @param {object}   calendarMeta
 */
export default function TeamMemberPlanCard({
  member, schedule, module = "daily", onScheduleUpdate, onRestored,
  enableDayActions = false, calMap, calendarMeta, teamName = "",
  sharedWeeklyPlan = null,
  focusWeek = null, focusDay = null, focusToken = null,
  activityOverrides = null,
}) {
  const { notify } = useAppNotify();
  const [feedback, setFeedback] = useState("");
  const [updating, setUpdating] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [histTick, setHistTick] = useState(0);
  const [doneRows, setDoneRows] = useState(new Set());
  const [subTaskDone, setSubTaskDone] = useState(new Set());
  const [activeWeek, setActiveWeek] = useState(null);
  const [activeDayKey, setActiveDayKey] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [dailyLayout, setDailyLayout] = useState("weekGrid");
  const [weeklyOverride, setWeeklyOverride] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const fileBase = [
    String(teamName || "").trim().replace(/[^\w\-]+/g, "_"),
    String(member?.name || "member").trim().replace(/[^\w\-]+/g, "_"),
  ].filter(Boolean).join("_") || "member";

  const cellText = (val) => String(val ?? "").replace(/\\n/g, "\n");

  const memberDays = useMemo(() => {
    const mid = String(member?.id || "");
    return (schedule || [])
      .map(d => ({ d, a: (d.assignments || []).find(x => String(x.memberId) === mid) }))
      .filter(x => x.a);
  }, [schedule, member]);

  const weeks = useMemo(() => [...new Set(memberDays.map(x => x.d.week))].sort((a, b) => a - b), [memberDays]);
  const currentWeek = weeks.includes(activeWeek) ? activeWeek : weeks[0];
  const daysThisWeek = memberDays.filter(x => x.d.week === currentWeek);
  const getDayKey = (d) => (d ? (d.dayId ?? d.day) : null);
  const currentDayKey = daysThisWeek.some(x => getDayKey(x.d) === activeDayKey) ? activeDayKey : getDayKey(daysThisWeek[0]?.d);

  useEffect(() => { setDoneRows(new Set()); setSubTaskDone(new Set()); }, [member?.id, currentDayKey]);
  useEffect(() => {
    setWeeklyOverride(null);
    setUndoStack([]);
    setRedoStack([]);
  }, [member?.id, module]);

  const memberDailyPlan = useMemo(() => {
    if (module !== "daily" || !member?.id) return null;
    return {
      module: "daily",
      columns: ["Day", "Time", "Activity", "Task / Content"],
      rows: memberFlatRows(schedule, member.id),
    };
  }, [module, schedule, member?.id]);

  const syncedWeekly = useMemo(() => {
    if (module !== "weekly" || !member?.id) return null;
    return buildWeeklyFromDaily({ rows: memberFlatRows(schedule, member.id) }, sharedWeeklyPlan);
  }, [module, schedule, member?.id, sharedWeeklyPlan]);

  const weeklyRows = weeklyOverride
    || (syncedWeekly?.rows?.length ? syncedWeekly.rows : null)
    || (sharedWeeklyPlan?.rows?.length ? sharedWeeklyPlan.rows : null)
    || (member?.id ? buildMemberWeeklyRows(schedule, member.id) : []);

  const weeklyCols = syncedWeekly?.rows?.length
    ? syncedWeekly.columns
    : (sharedWeeklyPlan?.columns?.length ? sharedWeeklyPlan.columns : WEEKLY_COLS);

  if (!member) return null;

  function toggleDone(ri) {
    setDoneRows(prev => { const n = new Set(prev); n.has(ri) ? n.delete(ri) : n.add(ri); return n; });
  }
  function toggleSubtask(ri, si) {
    const key = `${ri}-${si}`;
    setSubTaskDone(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  // ── Excel / CSV — daily (1 sheet per day, matches ModuleCard exactly) ────
  const downloadDailyXLSX = () => {
    const sourceRows = memberFlatRows(schedule, member.id);
    const rows = dailyLayout === "dayList" ? applyCurrentActivityTitles(sourceRows, activityOverrides) : sourceRows;
    if (!rows.length) return;
    const wb = XLSX.utils.book_new();
    const sheets = applyTeamDailyColumnPrefs(dailySheetsForLayout(dailyLayout, rows, null, null, visibleDailyBlueprint(activityOverrides || {}), true), dailyLayout);
    if (!sheets.length) return;
    appendDailySheetsToWorkbook(wb, XLSX, sheets);
    XLSX.writeFile(wb, `${fileBase}_daily_plan.xlsx`);
  };

  const downloadDailyCSV = () => {
    const sourceRows = memberFlatRows(schedule, member.id);
    const rows = dailyLayout === "dayList" ? applyCurrentActivityTitles(sourceRows, activityOverrides) : sourceRows;
    if (!rows.length) return;
    const sheets = applyTeamDailyColumnPrefs(dailySheetsForLayout(dailyLayout, rows, null, null, visibleDailyBlueprint(activityOverrides || {}), true), dailyLayout);
    const escCell = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
    let csvContent = "";
    sheets.forEach(({ sheetName, header, body }, idx) => {
      if (idx > 0) csvContent += "\r\n";
      csvContent += escCell(sheetName) + "\r\n";
      csvContent += header.map(escCell).join(",") + "\r\n";
      body.forEach((row) => { csvContent += row.map(escCell).join(",") + "\r\n"; });
    });
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileBase}_daily_plan.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Excel / CSV — weekly (single sheet, matches ModuleCard exactly) ──────
  const downloadWeeklyXLSX = () => {
    if (!weeklyRows.length) return;
    const wb = XLSX.utils.book_new();
    const body = weeklyRows.map((row) => (row || []).map((c) => cellText(c)));
    const ws = XLSX.utils.aoa_to_sheet([weeklyCols, ...body]);
    ws["!cols"] = weeklyCols.map((_, i) => {
      if (i === 0) return { wch: 14 };
      if (i === 1) return { wch: 12 };
      if (/milestone/i.test(String(weeklyCols[i] || ""))) return { wch: 55 };
      if (/exam/i.test(String(weeklyCols[i] || ""))) return { wch: 40 };
      if (/subject/i.test(String(weeklyCols[i] || ""))) return { wch: 40 };
      return { wch: 28 };
    });
    const milestoneIdx = weeklyCols.findIndex((c) => /milestone/i.test(String(c || "")));
    ws["!rows"] = [{ hpt: 22 }, ...body.map((row) => {
      const text = milestoneIdx >= 0 ? String(row[milestoneIdx] || "") : row.map(String).join("\n");
      const lines = Math.max(4, text.split(/\n/).length);
      return { hpt: Math.min(240, 14 * lines + 12) };
    })];
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let R = 1; R <= range.e.r; R++) {
      for (let C = 0; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[addr]) ws[addr].s = { alignment: { wrapText: true, vertical: "top" } };
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, String(member.name || "Weekly").slice(0, 31));
    XLSX.writeFile(wb, `${fileBase}_weekly_plan.xlsx`);
  };

  const downloadWeeklyCSV = () => {
    if (!weeklyRows.length) return;
    downloadCSVGeneric(weeklyCols, weeklyRows.map((r) => r.map(cellText)), `${fileBase}_weekly_plan.csv`);
  };

  function downloadCSVGeneric(cols, rows, filename) {
    const escCell = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
    let csvContent = cols.map(escCell).join(",") + "\r\n";
    rows.forEach(row => { csvContent += row.map(escCell).join(",") + "\r\n"; });
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Refine — reuses the same stateless /api/refine endpoint ModuleCard
  // uses; safe because it never touches the DB, it just returns refined
  // text for whatever rows we send it. ──────────────────────────────────────
  const handleRefine = async () => {
    if (!feedback.trim()) return;
    setUpdating(true);
    try {
      if (module === "weekly") {
        const plan = { module: "weekly", columns: weeklyCols, rows: weeklyRows };
        const res = await refinePlan("weekly", plan, feedback.trim());
        if (res.success) {
          setWeeklyOverride(res.plan.rows);
          try {
            await saveVersion(res.plan, "Manual Edit", `Team refine weekly (${member.name}): ${feedback.trim()}`, {
              memberName: String(member.name || "").trim() || "Solo",
              memberEmail: String(member.email || "").trim() || undefined,
              email: member.email || undefined,
            }, { trigger: "team_refine", changedBy: "Student" });
            setHistTick((t) => t + 1);
          } catch (_) {}
          setFeedback("");
        } else {
          notify({ type: "error", title: "Refine failed", message: res.error || "unknown error" });
        }
      } else {
        const rows = memberFlatRows(schedule, member.id);
        const plan = { module: "daily", columns: ["Day", "Time", "Activity", "Task / Content"], rows };
        const res = await refinePlan("daily", plan, feedback.trim());
        if (res.success) {
          const updated = applyRefinedRows(schedule, member.id, res.plan.rows);
          onScheduleUpdate(updated);
          try {
            await saveVersion(res.plan, "Manual Edit", `Team refine daily (${member.name}): ${feedback.trim()}`, {
              memberName: String(member.name || "").trim() || "Solo",
              memberEmail: String(member.email || "").trim() || undefined,
              email: member.email || undefined,
            });
            setHistTick((t) => t + 1);
          } catch (_) {}
          setFeedback("");
        } else {
          notify({ type: "error", title: "Refine failed", message: res.error || "unknown error" });
        }
      }
    } catch (e) {
      notify({ type: "error", title: "Update failed", message: e.message || "Update failed" });
    }
    setUpdating(false);
  };

  const handleSelfDayRegenerated = (dayId, newRows) => {
    if (!onScheduleUpdate || !member?.id) return;
    const mid = String(member.id);
    const updated = (schedule || []).map((day) => {
      const key = day.dayId || day.dayName || String(day.day);
      if (key !== dayId) return day;
      return {
        ...day,
        assignments: (day.assignments || []).map((a) => {
          if (String(a.memberId) !== mid) return a;
          const rows = (newRows || []).map((r) => {
            if (Array.isArray(r)) return { time: r[1], activity: r[2], content: r[3] };
            return { time: r.time, activity: r.activity, content: r.content };
          });
          return { ...a, rows };
        }),
      };
    });
    onScheduleUpdate(updated);
  };

  const handleMemberPlanChange = (nextPlan) => {
    if (!onScheduleUpdate || !member?.id || !nextPlan?.rows) return;
    // Snapshot current member daily plan for undo
    if (memberDailyPlan) {
      setUndoStack((s) => [...s.slice(-39), JSON.parse(JSON.stringify(memberDailyPlan))]);
      setRedoStack([]);
    }
    const updated = applyRefinedRows(schedule, member.id, nextPlan.rows);
    onScheduleUpdate(updated);
  };

  const handleWeeklyPlanChange = (next) => {
    if (!next?.rows) return;
    const currentRows = weeklyOverride
      || (member?.id ? buildMemberWeeklyRows(schedule, member.id) : []);
    setUndoStack((s) => [...s.slice(-39), { module: "weekly", rows: JSON.parse(JSON.stringify(currentRows)) }]);
    setRedoStack([]);
    setWeeklyOverride(next.rows);
  };

  const undoEdit = () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    if (module === "weekly") {
      const cur = { module: "weekly", rows: weeklyOverride || weeklyRows };
      setRedoStack((s) => [...s, JSON.parse(JSON.stringify(cur))]);
      setWeeklyOverride(prev.rows || prev);
    } else if (memberDailyPlan) {
      setRedoStack((s) => [...s, JSON.parse(JSON.stringify(memberDailyPlan))]);
      if (prev?.rows) {
        const updated = applyRefinedRows(schedule, member.id, prev.rows);
        onScheduleUpdate?.(updated);
      }
    }
  };

  const redoEdit = () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((s) => s.slice(0, -1));
    if (module === "weekly") {
      const cur = { module: "weekly", rows: weeklyOverride || weeklyRows };
      setUndoStack((s) => [...s, JSON.parse(JSON.stringify(cur))]);
      setWeeklyOverride(next.rows || next);
    } else if (memberDailyPlan) {
      setUndoStack((s) => [...s, JSON.parse(JSON.stringify(memberDailyPlan))]);
      if (next?.rows) {
        const updated = applyRefinedRows(schedule, member.id, next.rows);
        onScheduleUpdate?.(updated);
      }
    }
  };

  return (
    <Card style={{ animation: "fadeIn 0.3s ease" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <p style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{member.name}'s {module === "weekly" ? "Weekly" : "Daily"} Plan</p>
          <p style={{ fontSize: 12, color: C.muted }}>
            {member.projectRole
              ? `Project role: ${typeof member.projectRole === "object" ? member.projectRole.title : member.projectRole}`
              : (member.targetRole ? `Role: ${member.targetRole}` : member.skillTag)}
            {member.targetCompany ? ` · Target: ${member.targetCompany}` : ""}
            {enableDayActions && module === "daily" ? " · Sync, Lock & Disruption available below" : ""}
          </p>
        </div>
      </div>

      {module === "weekly" && (
        <div style={{
          marginBottom: 10, padding: "10px 12px", borderRadius: 8,
          background: "#ECFDF5", border: "1px solid #A7F3D0", fontSize: 12.5, color: "#065F46",
        }}>
          <strong>{member.name}'s individual weekly plan</strong>
          {" — built from their own daily tasks so roles stay clear. "}
          Use <strong>Team (shared)</strong> above for the common team week view.
        </div>
      )}

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, flexWrap: "wrap", marginBottom: 10,
        padding: "10px 12px", borderRadius: 8,
        background: editMode ? TABLE.editBanner : "#F3F4F6",
        border: `1px solid ${editMode ? "#93C5FD" : C.border}`,
      }}>
        <div style={{ fontSize: 12.5, color: C.text, flex: 1, minWidth: 160 }}>
          <strong>{editMode ? "Edit mode ON" : "Need a correction?"}</strong>
          {" "}
          {editMode
            ? "Click a task, edit text, Save. Undo / Redo restores previous edits."
            : "Edit schedule here (one place) to fix or add lines."}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn small variant="ghost" disabled={!undoStack.length} onClick={undoEdit}>
            <i className="ti ti-arrow-back-up" style={{ fontSize: 14 }} /> Undo
          </Btn>
          <Btn small variant="ghost" disabled={!redoStack.length} onClick={redoEdit}>
            <i className="ti ti-arrow-forward-up" style={{ fontSize: 14 }} /> Redo
          </Btn>
          <Btn small variant={editMode ? "primary" : "outline"} onClick={() => setEditMode((v) => !v)}>
            <i className="ti ti-pencil" style={{ fontSize: 14 }} />
            {editMode ? "Done editing" : "Edit schedule"}
          </Btn>
        </div>
      </div>

      {module === "weekly" ? (
        weeklyRows.length ? (
          <div style={{ marginBottom: 14 }}>
            <PlanTable
              plan={{ module: "weekly", columns: weeklyCols, rows: weeklyRows }}
              editMode={editMode}
              onPlanChange={handleWeeklyPlanChange}
            />
          </div>
        ) : (
          <div style={{ padding: "24px 16px", textAlign: "center", color: C.muted, fontSize: 12.5, marginBottom: 14 }}>
            No weekly plan yet — generate plans and Sync Team first.
          </div>
        )
      ) : memberDailyPlan?.rows?.length > 0 ? (
        <div style={{ marginBottom: 14 }}>
          <DailyPlanTable
            plan={memberDailyPlan}
            calMap={calMap}
            calendarMeta={calendarMeta}
            onDayRegenerated={enableDayActions ? handleSelfDayRegenerated : undefined}
            editMode={editMode}
            onPlanChange={handleMemberPlanChange}
            focusWeek={focusWeek}
            focusDay={focusDay}
            focusToken={focusToken}
            scheduleLayout={dailyLayout}
            onScheduleLayoutChange={setDailyLayout}
            memberMeta={{
              memberName: String(member?.name || "").trim() || "Solo",
              name: member?.name,
              email: member?.email,
              memberEmail: member?.email,
            }}
          />
        </div>
      ) : (
        <div style={{ padding: "24px 16px", textAlign: "center", color: C.muted, fontSize: 12.5, marginBottom: 14 }}>
          No schedule rows found for {member.name}. Click Re-sync Team.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <Btn small variant="outline" onClick={module === "weekly" ? downloadWeeklyXLSX : downloadDailyXLSX}>
          <i className="ti ti-file-spreadsheet" style={{ fontSize: 14 }} />
          {module === "weekly"
            ? "Excel"
            : dailyLayout === "dayList"
              ? "Excel (day list)"
              : "Excel (week table)"}
        </Btn>
        <Btn small variant="ghost" onClick={module === "weekly" ? downloadWeeklyCSV : downloadDailyCSV}>
          <i className="ti ti-download" style={{ fontSize: 14 }} /> CSV
        </Btn>
        <Btn small variant="ghost" onClick={() => setShowHist(!showHist)}>
          <i className="ti ti-history" style={{ fontSize: 14 }} /> History
        </Btn>
      </div>

      <PlanVersionPanel
        key={`${member?.id}-${histTick}`}
        open={showHist}
        onRestored={(plan, newVersion) => {
          onRestored?.(plan, newVersion);
        }}
      />

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8 }}>
          <i className="ti ti-sparkles" style={{ fontSize: 13, marginRight: 4, color: C.primary }} />
          Additional Instructions (refine {member.name}'s {module} plan)
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={feedback} onChange={e => setFeedback(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleRefine()}
            disabled={updating}
            placeholder='e.g. "Make DSA topics clearer with more examples"'
            style={{ flex: 1, padding: "9px 14px", fontSize: 13, border: `1.5px solid ${C.border}`, borderRadius: 8, outline: "none", color: C.text, background: C.surface }} />
          <Btn onClick={handleRefine} disabled={updating || !feedback.trim()} small>
            {updating ? "..." : "Refine →"}
          </Btn>
        </div>
      </div>
    </Card>
  );
}
